// src/components/charts/FoodBeverageCharts.jsx
import React from 'react';
import { Row, Col, Card, Table } from 'react-bootstrap';
import kpiService from '../../services/kpiService';
import { formatCurrency, formatNumber } from '../../utils/formatters';

/**
 * Componente que agrupa los datos de Food & Beverage (Imagen 4)
 * - Total ingresos F&B por área (tabla - izquierda)
 * - Márgen por área (tabla - centro)
 * - Productos más vendidos (tabla - derecha)
 */
const FoodBeverageCharts = ({ fechaInicio, fechaFin, empresaId }) => {
  const [foodBeverageData, setFoodBeverageData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await kpiService.getFoodBeverage({
          fechaInicio,
          fechaFin,
          empresaId,
        });

        if (response.success) {
          setFoodBeverageData(response.data);
        }
      } catch (err) {
        console.error('Error fetching food & beverage data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fechaInicio, fechaFin, empresaId]);

  if (loading) {
    return (
      <Row className="g-3 mb-4">
        <Col xs={12} className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3 text-muted">Cargando datos...</p>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="g-3 mb-4">
      {/* Total ingresos F&B por área */}
      <Col lg={4}>
        {foodBeverageData?.ingresosPorArea && (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Total ingresos F&B por área</h5>
              <Table hover responsive className="mb-0">
                <tbody>
                  {foodBeverageData.ingresosPorArea.map((item, idx) => (
                    <tr key={idx}>
                      <td className="fw-medium">{item.area}</td>
                      <td className="text-end">
                        <div className="fw-semibold">{formatCurrency(item.valor)}</div>
                        <div className="small text-muted">% sobre total</div>
                      </td>
                      <td className="text-end" style={{ width: '100px' }}>
                        <div className="d-flex align-items-center gap-2">
                          <span 
                            className="badge"
                            style={{ backgroundColor: item.color }}
                          >
                            + {item.porcentaje}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}
      </Col>

      {/* Márgen por área */}
      <Col lg={4}>
        {foodBeverageData?.margenPorArea && (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Márgen por área</h5>
              <Table hover responsive className="mb-0">
                <tbody>
                  {foodBeverageData.margenPorArea.map((item, idx) => (
                    <tr key={idx}>
                      <td className="fw-medium">{item.area}</td>
                      <td className="text-end">
                        <div className="fw-semibold">{item.porcentaje}%</div>
                        <div className="small text-muted">% sobre total</div>
                      </td>
                      <td className="text-end" style={{ width: '100px' }}>
                        <div className="d-flex align-items-center gap-2">
                          <span 
                            className="badge"
                            style={{ backgroundColor: item.color }}
                          >
                            + {item.variacion}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}
      </Col>

      {/* Productos más vendidos */}
      <Col lg={4}>
        {foodBeverageData?.productosVendidos && (
          <Card>
            <Card.Body>
              <h5 className="mb-4">Productos más vendidos</h5>
              <Table hover responsive className="mb-0">
                <tbody>
                  {foodBeverageData.productosVendidos.map((item, idx) => (
                    <tr key={idx}>
                      <td className="fw-medium">{item.producto}</td>
                      <td className="text-end">
                        <div className="fw-semibold">{formatNumber(item.cantidad)}</div>
                        <div className="small text-muted">% sobre total</div>
                      </td>
                      <td className="text-end" style={{ width: '80px' }}>
                        <span className="badge bg-success">
                          + {item.porcentaje}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}
      </Col>
    </Row>
  );
};

export default FoodBeverageCharts;